import { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'next-themes';
import { Elements } from '@stripe/react-stripe-js';
import { getStripe, isStripeConfigured } from '@/lib/stripe';
import { useFetch } from '@/hooks/use-fetch';
import { persistConfirmedCard, type BillingOutcome } from '@/lib/billing-card';
import { CardCapture, buildAppearance, type CardCaptureApi } from './card-capture';
import {
  useMintSetupIntent,
  usePaymentMethodsInvalidate,
  useSubscriptionInvalidate,
  type MintSetupIntentVars,
  type PaymentMethod,
} from './queries';
import { useAlertPhone } from '@/pages/launchpad/use-alert-phone';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

export function AddCardDialog({ open, orgId, onOpenChange, onSaved, onOutcome, notice, collectPhone = false, mintVars }: {
  open: boolean; orgId: string; onOpenChange: (v: boolean) => void; onSaved: () => void;
  /** AIT-505 — what saving the card actually did server-side (plain save vs a
   *  paused-resume charge that succeeded or failed). */
  onOutcome?: (outcome: BillingOutcome) => void;
  /** What happens to this customer's money once the card is on file. The
   *  caller knows the context (they picked a plan, or they are just covering
   *  the end of a trial), and saying nothing here is how a card turns into a
   *  surprise charge. */
  notice?: string;
  /** Plans-page entry only: bundle the alert-phone capture + OTP into the
   *  save flow. The Billing page's Add Card stays a plain card dialog. */
  collectPhone?: boolean;
  /** AIT-505 — attribution + plan pick stamped on the SetupIntent's metadata
   *  at mint (`targetSlug`/`interval`/`source`). Read once when the dialog
   *  opens (and again on remint); the caller sets the underlying state
   *  BEFORE flipping `open`, so this always reflects the click that opened
   *  the dialog. */
  mintVars?: MintSetupIntentVars;
}) {
  const { resolvedTheme } = useTheme();
  // AIT-494 — the phone row shows only while the user has no verified alert
  // phone; a verified customer gets the plain card form.
  const alertPhone = useAlertPhone();
  const fetchWithAuth = useFetch();
  const invalidateMethods = usePaymentMethodsInvalidate(orgId);
  const invalidateSubscription = useSubscriptionInvalidate(orgId);
  // AIT-505 Task 7 — the authenticated org-billing implementation of
  // CardCaptureApi. CardCapture itself never touches fetchWithAuth/the
  // payment-methods query directly; this is the only place those hooks meet
  // the shared form.
  const api: CardCaptureApi = useMemo(() => ({
    async persistCard(setupIntent) {
      const result = await persistConfirmedCard(fetchWithAuth, orgId, setupIntent);
      // Mirrors the pre-extraction behavior exactly: a charge_failed outcome
      // does NOT invalidate the payment-methods cache here (the dialog stays
      // open on a fresh SetupIntent instead of showing the saved card).
      if (result.ok && result.billingOutcome !== 'charge_failed') {
        void invalidateMethods();
      }
      return result;
    },
    async sendOtp({ phone }) {
      // AIT-511: the card route, not the shared one — this modal has no channel
      // picker, so the server fans the first code out over every channel and
      // stores whichever one delivered.
      const res = await fetchWithAuth('/auth/phone/card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          // AIT-383: verifying is the consent signature for all categories —
          // the small print under the field states exactly that.
          consentOperational: true,
          consentProduct: true,
          consentMarketing: true,
          // No channel choice in this flow: the server fans a first
          // verification out over every channel and stores whichever one
          // delivered (AIT-511).
        }),
      });
      const body = (await res.json()) as { delivery?: string; message?: string };
      return { sent: res.ok && body.delivery === 'sent', message: body.message };
    },
    async verifyOtp(code) {
      const res = await fetchWithAuth('/auth/phone/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const body = (await res.json()) as { message?: string };
      return { ok: res.ok, message: body.message };
    },
    async listCards() {
      const res = await fetchWithAuth(`/organizations/${orgId}/billing/payment-methods`);
      if (!res.ok) return [];
      return (await res.json()) as PaymentMethod[];
    },
  }), [fetchWithAuth, orgId, invalidateMethods]);
  // The SetupIntent mint is a mutation, not a query — the secret must be
  // re-minted fresh on every open, never served from cache.
  const mint = useMintSetupIntent(orgId);
  const { mutate: mintIntent, reset: resetMint } = mint;
  const remint = () => mintIntent(mintVars ?? {});
  // AIT-505 — set when a save's resume charge is declined. Owned HERE (not by
  // CardCapture's own `error` state) because remint() mints a SetupIntent with a
  // different id, which re-keys `<Elements>` below and unmounts/remounts the
  // whole card form — a child-local error would vanish the instant the new
  // secret arrives, mid-mint or not (review F6 wants the fresh PaymentElement;
  // this is what keeps the "why" visible through that remount).
  const [chargeFailedError, setChargeFailedError] = useState<string | null>(null);

  // The dialog is opened by the PARENT flipping `open` to true (a controlled prop).
  // A controlled prop change does NOT fire Dialog's onOpenChange, so the SetupIntent
  // MUST be minted from an effect on `open` — not inside onOpenChange — or Elements
  // never renders. Also reset on close so reopening re-mints a fresh intent.
  // The Retry button simply mints again. The explicit loading/error status
  // guarantees a failed or slow mint shows feedback, never a blank dialog.
  // mintVars intentionally omitted from deps: it reflects a snapshot of
  // caller state set right before `open` flips true, not a value this effect
  // should re-run for on every render (the caller updates it via its own
  // state, not by mutating the same object in place).
  useEffect(() => {
    setChargeFailedError(null);
    if (!open || !isStripeConfigured()) { resetMint(); return; }
    remint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orgId, mintIntent, resetMint]);

  const clientSecret = mint.data?.clientSecret ?? null;
  // AIT-494 — a build without the publishable key must show a real error, not
  // a PaymentElement that never renders (uncaught IntegrationError).
  const status: 'unconfigured' | 'loading' | 'error' | 'ready' = !isStripeConfigured()
    ? 'unconfigured'
    : mint.isError
      ? 'error'
      : mint.data
        ? 'ready'
        : 'loading';

  // While a save is in flight, swallow dismissals (Escape / backdrop / X):
  // closing mid-save would clear the parent's pending pick and toast "not
  // changed" while the card save may still land (Codex PR #260).
  const savingRef = useRef(false);
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && savingRef.current) return;
        onOpenChange(o);
      }}
    >
      {/* This dialog is tall (Stripe PaymentElement) and has no Select inside
          the card stage, so it can safely be its own scroll container. */}
      <DialogContent className="overflow-y-auto">
        <DialogHeader><DialogTitle>Add a credit card</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          {notice ?? "You won't be charged until you turn on automatic billing."}
        </p>
        {/* AIT-505 — rendered OUTSIDE the status/Elements branches below on
            purpose: a remint (triggered by this same outcome) transitions
            status through 'loading' and re-keys <Elements>, unmounting that
            whole subtree. This message must survive that. */}
        {chargeFailedError && (
          <p role="alert" className="text-sm text-destructive">{chargeFailedError}</p>
        )}
        {status === 'unconfigured' && (
          <p role="alert" className="text-sm text-destructive">
            Card entry is unavailable right now. Please contact support.
          </p>
        )}
        {status === 'loading' && (
          <div role="status" className="flex items-center justify-center py-6">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
            <span className="sr-only">Loading</span>
          </div>
        )}
        {status === 'error' && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-destructive">Couldn&apos;t start adding a card. Please try again.</p>
            <Button onClick={remint}>Retry</Button>
          </div>
        )}
        {status === 'ready' && clientSecret && (
          <Elements
            // A remint (Retry, or the charge_failed retry-with-another-card
            // path) produces a new clientSecret for a spent SetupIntent —
            // keying on it forces Elements/PaymentElement to remount fresh
            // instead of reusing state tied to the old intent (review F6).
            key={clientSecret}
            stripe={getStripe()}
            options={{
              clientSecret,
              appearance: buildAppearance(resolvedTheme === 'dark'),
              // Stripe renders in a cross-origin iframe — the app's bundled
              // Geist never reaches it, so hand it the same woff2 we bundle
              // (public/fonts). cssSrc must be absolute for the iframe.
              fonts: [{ cssSrc: `${window.location.origin}/fonts/geist-stripe.css` }],
            }}
          >
            <CardCapture
              api={api}
              needsPhone={collectPhone && !alertPhone.loading && !alertPhone.status?.verified}
              phoneStatusPending={collectPhone && alertPhone.loading}
              savingRef={savingRef}
              onSaved={() => {
                onSaved();
                void invalidateSubscription();
              }}
              onVerified={() => void alertPhone.refresh()}
              onClose={() => onOpenChange(false)}
              onOutcome={(outcome) => {
                onOutcome?.(outcome);
                if (outcome === 'charge_failed') {
                  setChargeFailedError('Your card was saved, but the charge failed. Try another card.');
                  // Codex round 7 (P2) — remint() below re-keys <Elements>,
                  // unmounting this CardCapture. If the FOLLOW-UP mint also
                  // fails, no CardCapture ever remounts to flip savingRef back
                  // to false, and onOpenChange's close guard above blocks
                  // Escape/backdrop/X forever. Release the lock before the
                  // remint/unmount can strand it.
                  savingRef.current = false;
                  remint();
                } else {
                  setChargeFailedError(null);
                }
              }}
            />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}
